import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import NavigationDrawer from './NavigationDrawer';
import SearchDialog from './SearchDialog';
import AIAssistDialog from './AIAssistDialog';

const DRAWER_WIDTH = 320;

interface AppLayoutProps {
  children: React.ReactNode;
  onSkillsFilter?: (skillIds: string[]) => void;
  onCourseSelect?: (courseName: string, coreSkills: string[]) => void;
  onDomainSelect?: (domain: string | null) => void;
  onSubjectSelect?: (subject: string | null) => void;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ 
  children, 
  onSkillsFilter,
  onCourseSelect,
  onDomainSelect,
  onSubjectSelect
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [searchOpen, setSearchOpen] = useState(false);
  const [aiAssistOpen, setAIAssistOpen] = useState(false);
  
  // Selected states
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);
  const [selectedCourseSkills, setSelectedCourseSkills] = useState<string[]>([]);

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleSearchOpen = () => {
    setSearchOpen(true);
  };

  const handleSearchClose = () => {
    setSearchOpen(false);
  };

  const handleAIAssistOpen = () => {
    setAIAssistOpen(true);
  };

  const handleAIAssistClose = () => {
    setAIAssistOpen(false);
  };

  const handleDomainSelect = (domain: string) => {
    // Toggle: if clicking same domain, clear it
    const newDomain = selectedDomain === domain ? null : domain;
    setSelectedDomain(newDomain);
    setSelectedSubject(null);
    setSelectedCourse(null);
    onDomainSelect?.(newDomain);
  };

  const handleSubjectSelect = (subject: string) => {
    // Toggle
    const newSubject = selectedSubject === subject ? null : subject;
    setSelectedSubject(newSubject);
    setSelectedCourse(null);
    onSubjectSelect?.(newSubject);
  };

  const handleCourseSelect = (courseName: string, coreSkills: string[]) => {
    // Toggle: if clicking same course, clear filter
    if (selectedCourse === courseName) {
      setSelectedCourse(null);
      setSelectedCourseSkills([]);
      onSkillsFilter?.([]);
    } else {
      setSelectedCourse(courseName);
      setSelectedCourseSkills(coreSkills);
      // Pass skill names up — GraphExplorer will match them to CHISG node names
      onSkillsFilter?.(coreSkills);
    }
  };

  const handleSearchResults = (skillIds: string[]) => {
    onSkillsFilter?.(skillIds);
    setSearchOpen(false);
  };

  const handleAIResults = (skillIds: string[]) => {
    onSkillsFilter?.(skillIds);
    setAIAssistOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: '#1D1D29',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Skills Map Platform
          </Typography>
          <Tooltip title="Back to Portfolio">
            <IconButton
              color="inherit"
              component="a"
              href="/"
              aria-label="back to portfolio"
              sx={{
                ml: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <HomeIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Navigation Drawer */}
      <NavigationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={DRAWER_WIDTH}
        variant={isMobile ? 'temporary' : 'persistent'}
        selectedDomain={selectedDomain}
        selectedSubject={selectedSubject}
        selectedCourse={selectedCourse}
        onDomainSelect={handleDomainSelect}
        onSubjectSelect={handleSubjectSelect}
        onCourseSelect={handleCourseSelect}
        onSearchClick={handleSearchOpen}
        onAIAssistClick={handleAIAssistOpen}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          mt: '64px', // AppBar height
          // ml: Removed double margin logic. Drawer is in flex flow.
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          height: 'calc(100vh - 64px)',
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>

      {/* Search Dialog */}
      <SearchDialog
        open={searchOpen}
        onClose={handleSearchClose}
        onResults={handleSearchResults}
      />

      {/* AI Assist Dialog */}
      <AIAssistDialog
        open={aiAssistOpen}
        onClose={handleAIAssistClose}
        onResults={handleAIResults}
      />
    </Box>
  );
};

export default AppLayout;
